import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { client, urlFor, blogQueries } from '../sanityClient'
import './blog-post.css'

const SITE_ORIGIN = /^https?:\/\/(www\.)?dataflows\.com\.au/i

const renderSpans = (block) => {
  const defs = {}
  ;(block.markDefs || []).forEach((def) => { if (def && def._key) defs[def._key] = def })

  return (block.children || [])
    .filter((child) => child && typeof child.text === 'string')
    .map((child, i) => {
      let node = child.text
      const marks = child.marks || []
      marks.forEach((mark) => {
        if (mark === 'strong') node = <strong>{node}</strong>
        else if (mark === 'em') node = <em>{node}</em>
        else if (mark === 'code') node = <code>{node}</code>
      })
      const link = marks.map((m) => defs[m]).find((d) => d && d._type === 'link' && d.href)
      if (link) {
        const internal = link.href.startsWith('/') || SITE_ORIGIN.test(link.href)
        node = internal
          ? <Link to={link.href.replace(SITE_ORIGIN, '') || '/'} className="blog-post-link">{node}</Link>
          : <a href={link.href} target="_blank" rel="noopener noreferrer" className="blog-post-link">{node}</a>
      }
      return <React.Fragment key={child._key || i}>{node}</React.Fragment>
    })
}

const renderBlock = (block, key) => {
  const content = renderSpans(block)
  switch (block.style) {
    case 'h1': return <h1 key={key} className="blog-post-h1">{content}</h1>
    case 'h2': return <h2 key={key} className="blog-post-h2">{content}</h2>
    case 'h3': return <h3 key={key} className="blog-post-h3">{content}</h3>
    case 'h4': return <h4 key={key} className="blog-post-h4">{content}</h4>
    case 'blockquote': return <blockquote key={key} className="blog-post-blockquote">{content}</blockquote>
    default: return <p key={key} className="blog-post-paragraph">{content}</p>
  }
}

// Consecutive list-item blocks become one <ul>/<ol>; higher levels nest inside the previous item.
const renderList = (items, level, keyPrefix) => {
  const Tag = items[0].listItem === 'number' ? 'ol' : 'ul'
  const out = []
  let i = 0
  while (i < items.length) {
    const item = items[i]
    const children = []
    let j = i + 1
    while (j < items.length && (items[j].level || 1) > level) children.push(items[j++])
    out.push(
      <li key={item._key || `${keyPrefix}-${i}`} className="blog-post-list-item">
        {renderSpans(item)}
        {children.length > 0 && renderList(children, level + 1, `${keyPrefix}-${i}`)}
      </li>
    )
    i = j
  }
  return <Tag key={keyPrefix} className="blog-post-list">{out}</Tag>
}

const renderBody = (body) => {
  const out = []
  let i = 0
  while (i < body.length) {
    const block = body[i]
    if (!block || !block._type) { i++; continue }

    if (block._type === 'block' && block.listItem) {
      const items = []
      while (i < body.length && body[i] && body[i]._type === 'block' && body[i].listItem &&
             (items.length === 0 || (body[i].level || 1) > 1 || body[i].listItem === items[0].listItem)) {
        items.push(body[i++])
      }
      out.push(renderList(items, 1, `list-${i}`))
      continue
    }

    if (block._type === 'block' && Array.isArray(block.children)) {
      const text = block.children.map((c) => (c && c.text) || '').join('')
      if (text.trim()) out.push(renderBlock(block, block._key || i))
    } else if (block._type === 'table' && Array.isArray(block.rows) && block.rows.length > 1) {
      const [head, ...rest] = block.rows
      out.push(
        <figure key={block._key || i} className="blog-post-table-wrap">
          <table className="blog-post-table">
            <thead>
              <tr>{(head.cells || []).map((c, ci) => <th key={ci} scope="col">{c}</th>)}</tr>
            </thead>
            <tbody>
              {rest.map((row, ri) => (
                <tr key={row._key || ri}>
                  {(row.cells || []).map((c, ci) => (ci === 0 ? <th key={ci} scope="row">{c}</th> : <td key={ci}>{c}</td>))}
                </tr>
              ))}
            </tbody>
          </table>
          {block.caption && <figcaption className="blog-post-table-caption">{block.caption}</figcaption>}
        </figure>
      )
    } else if (block._type === 'image' && block.asset) {
      out.push(
        <div key={block._key || i} className="blog-post-content-image">
          <img src={urlFor(block).width(800).url()} alt={block.alt || 'Blog post image'} loading="lazy" />
          {block.caption && <figcaption className="blog-post-image-caption">{block.caption}</figcaption>}
        </div>
      )
    }
    i++
  }
  return out
}

const BlogPost = () => {
  const { slug } = useParams()
  const [post, setPost] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [notFound, setNotFound] = useState(false)

  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true)
      setNotFound(false)
      // One retry: a single failed request (common in search-engine renderers) must not look like a missing post.
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          const fetchedPost = await client.fetch(blogQueries.getPostBySlug, { slug })
          if (!fetchedPost) {
            setNotFound(true)
            setError('Blog post not found.')
          } else {
            setPost(fetchedPost)
            setError(null)
            if (typeof window !== 'undefined') {
              window.prerenderReady = true
            }
          }
          setLoading(false)
          return
        } catch (err) {
          console.error('Error fetching blog post:', err)
          if (attempt === 0) await new Promise((r) => setTimeout(r, 1500))
        }
      }
      setError('Failed to load blog post. Please try again later.')
      setLoading(false)
    }

    if (slug) {
      fetchPost()
    }
  }, [slug])

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  // SEO meta data with fallbacks
  const getMetaTitle = () => {
    if (post?.metaTitle) return post.metaTitle
    if (post?.title) return `${post.title} | DataFlows SMS`
    return 'DataFlows SMS Blog'
  }

  const getMetaDescription = () => {
    if (post?.metaDescription) return post.metaDescription
    if (post?.excerpt) return post.excerpt
    return 'Read the latest insights and tips about SMS marketing for Australian businesses from DataFlows.'
  }

  const getCanonicalUrl = () => {
    return `https://dataflows.com.au/blog/${slug}`
  }

  const getImageUrl = () => {
    if (post?.mainImage) {
      return urlFor(post.mainImage).width(1200).height(630).url()
    }
    return 'https://dataflows.com.au/dataflows-high-resolution-logo-200h.png'
  }

  if (loading) {
    return (
      <div className="blog-post-container">
        <div className="blog-post-loading">
          <div className="loading-spinner"></div>
          <p>Loading blog post...</p>
        </div>
      </div>
    )
  }

  if (error || !post) {
    return (
      <div className="blog-post-container">
        {notFound && (
          <Helmet>
            <meta name="robots" content="noindex, follow" />
          </Helmet>
        )}
        <div className="blog-post-error">
          <h1>Oops! Something went wrong</h1>
          <p>{error || 'Blog post not found.'}</p>
          <Link to="/blog" className="blog-post-back-button">
            ← Back to Blog
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="blog-post-container">
      <Helmet>
        <title>{getMetaTitle()}</title>
        <meta name="description" content={getMetaDescription()} />
        <link rel="canonical" href={getCanonicalUrl()} />
        <meta property="og:title" content={getMetaTitle()} />
        <meta property="og:description" content={getMetaDescription()} />
        <meta property="og:image" content={getImageUrl()} />
        <meta property="og:url" content={getCanonicalUrl()} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={getMetaTitle()} />
        <meta name="twitter:description" content={getMetaDescription()} />
        <meta name="twitter:image" content={getImageUrl()} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          "headline": post?.title,
          "description": getMetaDescription(),
          "image": getImageUrl(),
          "url": getCanonicalUrl(),
          "datePublished": post?.publishedAt,
          "dateModified": post?._updatedAt || post?.publishedAt,
          "author": { "@type": "Organization", "name": "DataFlows", "url": "https://dataflows.com.au" },
          "publisher": {
            "@type": "Organization",
            "name": "DataFlows",
            "logo": { "@type": "ImageObject", "url": "https://dataflows.com.au/dataflows-high-resolution-logo-200h.png" }
          },
          "mainEntityOfPage": { "@type": "WebPage", "@id": getCanonicalUrl() }
        })}</script>
      </Helmet>
      <article className="blog-post">
        {/* Navigation */}
        <nav className="blog-post-nav">
          <Link to="/blog" className="blog-post-back-link">
            ← Back to Blog
          </Link>
        </nav>

        {/* Header */}
        <header className="blog-post-header">
          {post.mainImage && (
            <div className="blog-post-hero-image">
              <img 
                src={urlFor(post.mainImage).width(1200).height(600).url()} 
                alt={post.title}
                loading="eager"
              />
            </div>
          )}
          <div className="blog-post-header-content">
            <div className="blog-post-meta">
              <time className="blog-post-date">
                {formatDate(post.publishedAt)}
              </time>
              {post.estimatedReadingTime && (
                <span className="blog-post-reading-time">
                  {post.estimatedReadingTime} min read
                </span>
              )}
            </div>
            <h1 className="blog-post-title">{post.title}</h1>
            {post.excerpt && (
              <p className="blog-post-excerpt">{post.excerpt}</p>
            )}
          </div>
        </header>

        {/* Content */}
        <div className="blog-post-content">
          {post.body && Array.isArray(post.body) && renderBody(post.body)}
        </div>

        {/* Footer */}
        <footer className="blog-post-footer">
          <div className="blog-post-cta">
            <h3>Ready to boost your business with SMS marketing?</h3>
            <p>Join thousands of businesses using DataFlows to connect with their customers.</p>
            <a 
              href="https://sms.dataflows.com.au/register" 
              target="_blank" 
              rel="noopener noreferrer"
              className="blog-post-cta-button"
            >
              Get Started Today
            </a>
          </div>
          <Link to="/blog" className="blog-post-back-button">
            ← Back to Blog
          </Link>
        </footer>
      </article>
    </div>
  )
}

export default BlogPost 